import { AvailableLanguage } from '@/models/AvailableLanguage';
import { Evaluation } from '@/models/Evaluation';
import { EvaluationLesson, Lesson } from '@/models/Lesson';
import { Reducer } from 'redux';
import { PURGE } from 'redux-persist';
import { Lessons, LessonsState, LessonTypes } from './types';

const INITIAL_STATE: LessonsState = {
    data: {} as Lessons,
    currentData: undefined,
    evaluation: {} as Evaluation,
    loading: true,
    hasError: false,
    error: undefined,
    success: false,
    mediaUrl: undefined,
    externalUrlMetadata: undefined,
    successOnPostEvaluation: false,
};

const reducer: Reducer<LessonsState> = (state: LessonsState = INITIAL_STATE, action) => {
    switch (action.type) {
        case PURGE:
            return { ...INITIAL_STATE };
        case LessonTypes.GET_LESSON_REQUEST:
            if (action.payload.refreshOnlyPath) {
                return { ...state };
            }
            return { ...state, loading: true };
        case LessonTypes.GET_LESSON_SUCCESS:
            return {
                ...state,
                loading: false,
                currentData: action.payload.lesson ? action.payload.lesson : action.payload,
                evaluation: action.payload.evaluation ? action.payload.evaluation : {},
            };

        case LessonTypes.GET_LESSON_PATH_SUCCESS:
            const currentData: Lesson = { ...state.currentData } as Lesson;
            if (currentData && currentData.content) {
                const oldPath: string = currentData.content.path;
                currentData.path = action.payload;
                currentData.content.path = action.payload;

                if (currentData.content.availableLanguages) {
                    const availableLanguage: AvailableLanguage = currentData.content.availableLanguages.find(
                        (available: AvailableLanguage) => available.path === oldPath,
                    ) as AvailableLanguage;

                    if (availableLanguage) {
                        availableLanguage.path = action.payload;
                    }
                }
            }
            return {
                ...state,
                loading: false,
                currentData,
            };
        case LessonTypes.GET_LESSON_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
                data: {} as Lessons,
            };
        case LessonTypes.GET_LESSONS_REQUEST:
            return { ...state, loading: true };
        case LessonTypes.GET_LESSONS_SUCCESS:
            return {
                ...state,
                loading: false,
                data: { ...state.data, lessons: action.payload },
            };
        case LessonTypes.GET_LESSONS_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
                data: {} as Lessons,
            };
        case LessonTypes.POST_LESSON_REQUEST:
            return { ...state, success: false, loading: true };
        case LessonTypes.POST_LESSON_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
                currentData: action.payload,
            };
        case LessonTypes.POST_LESSON_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                success: false,
                error: action.payload,
            };
        case LessonTypes.POST_EVALUATION_REQUEST:
            return { ...state, successOnPostEvaluation: false, loading: true };
        case LessonTypes.POST_EVALUATION_SUCCESS:
            return {
                ...state,
                loading: false,
                successOnPostEvaluation: true,
                currentData: action.payload,
                data: { ...state.data, lessons: [...state.data.lessons, action.payload] },
            };
        case LessonTypes.POST_EVALUATION_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                successOnPostEvaluation: false,
                error: action.payload,
            };
        case LessonTypes.PUT_LESSON_REQUEST:
            return { ...state, success: false, loading: true };
        case LessonTypes.PUT_LESSON_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
            };
        case LessonTypes.PUT_LESSON_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
            };
        case LessonTypes.DELETE_LESSON_REQUEST:
            return { ...state, loading: true };
        case LessonTypes.DELETE_LESSON_SUCCESS:
            return {
                ...state,
                loading: false,
            };
        case LessonTypes.DELETE_LESSON_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
            };

        case LessonTypes.GET_LESSON_CLEAR:
        case LessonTypes.POST_LESSON_CLEAR:
        case LessonTypes.POST_EVALUATION_CLEAR:
        case LessonTypes.PUT_LESSON_CLEAR:
        case LessonTypes.DELETE_LESSON_CLEAR:
            return INITIAL_STATE;
        case LessonTypes.SET_LESSON_EXTERNAL_LINK_METADATA_REQUEST:
            return { ...state, loading: true };
        case LessonTypes.SET_LESSON_EXTERNAL_LINK_METADATA_SUCCESS:
            return { ...state, loading: false, hasError: false, externalUrlMetadata: action.payload };
        case LessonTypes.SET_LESSON_EXTERNAL_LINK_METADATA_FAILURE:
            return { ...state, loading: false, hasError: true, error: action.payload };
        case LessonTypes.START_LESSON_REQUEST:
            return { ...state, hasError: false };
        case LessonTypes.START_LESSON_SUCCESS:
            return { ...state, mediaUrl: action.payload, success: true };
        case LessonTypes.START_LESSON_FAILURE:
            return { ...state, loading: false, hasError: true };
        case LessonTypes.FINISH_LESSON_REQUEST:
            return { ...state, hasError: false };
        case LessonTypes.FINISH_LESSON_SUCCESS:
            //TODO: Implementar FINISH_CONTENT_SUCCESS
            return INITIAL_STATE;
        case LessonTypes.FINISH_LESSON_FAILURE:
            return { ...state, loading: false, hasError: true };
        case LessonTypes.SET_CURRENT_LESSON:
            return { ...state, currentData: { ...state.currentData, content: action.payload }, loading: false, hasError: false };
        case LessonTypes.SET_CURRENT_LESSON_FOR_EDITION:
            return { ...state, currentData: action.payload, loading: false, hasError: false, success: false };
        case LessonTypes.INCREASE_LESSON_QUESTIONS_AMOUNT:
            return {
                ...state,
                data: {
                    ...state.data,
                    lessons: state.data.lessons.map((lesson) => {
                        if (lesson.id === action.payload.lessonId && 'questionsAmount' in lesson) {
                            if ((lesson as EvaluationLesson).questionsAmount) {
                                (lesson as EvaluationLesson).questionsAmount += 1;
                            } else {
                                (lesson as EvaluationLesson).questionsAmount = 1;
                            }
                        }
                        return lesson;
                    }),
                },
            };
        case LessonTypes.DECREASE_LESSON_QUESTIONS_AMOUNT:
            return {
                ...state,
                data: {
                    ...state.data,
                    lessons: state.data.lessons.map((lesson) => {
                        if (lesson.id === action.payload.lessonId && 'questionsAmount' in lesson) {
                            if ((lesson as EvaluationLesson).questionsAmount && (lesson as EvaluationLesson).questionsAmount > 0) {
                                (lesson as EvaluationLesson).questionsAmount -= 1;
                            }
                        }
                        return lesson;
                    }),
                },
            };
        case LessonTypes.CLEAR_START_SUCCESS:
            return { ...state, success: false };
        default:
            return state;
    }
};

export default reducer;
